import React, { Component } from 'react';
import ReactGA from 'react-ga4';
import { withRouter } from 'react-router-dom';
import { adaptV4Theme } from '@mui/material/styles';
import { styled } from '@mui/system';
import NiceModal from '@ebay/nice-modal-react';

import { values, isEmpty, pick, assign, get, reduce } from 'lodash';
import queryString from 'query-string';

import Routes from './routes';
import SnackbarProvider from './context/SnackbarProvider';
import { createTheme } from '@mui/material/styles';

import { ThemeProvider } from 'common';
import { THEME_DEFAULTS } from 'common/constants/theme';
import Loading from 'common/components/Loading';

import Cookies from 'universal-cookie';
import { getCookieOptions } from 'common/utils/cookie';
import Api from './utils/Api';
import ApiContext from 'common/context/ApiContext';

import { UserContext } from 'common/context/UserContext';
import { ItinerariesContext } from 'common/context/ItinerariesContext';
import { MenuProvider } from './context/MenuContext';
import { ModalProvider } from 'react-modal-hook';

import { PDF_LIST } from 'common/constants/PdfList';
import { getPdfUrl } from 'common/helpers/content';

import i18n from './i18n';

import { fetchMediaContentImage } from 'common/helpers/content';
import { buildTheme } from 'utils/theme';
import * as Sentry from '@sentry/react';

import { UTM_COOKIE, UTM_GA, UTM, getItrvlCid } from 'itrvl-types';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from 'query';

import logger from 'itrvl-logger';
import CustomizationsLoader from './components/CustomizationsLoader';
const log = logger(__filename);

export const getTermsUrl = customizations => {
  const agencyTermsUrl = get(customizations, 'termsandconditions.0');
  return agencyTermsUrl ? getPdfUrl(agencyTermsUrl) : PDF_LIST.termsAndConditions;
};

export const handleTermsUrl = customizations => {
  // handle terms here so we can pass it around
  customizations.termsUrl = getTermsUrl(customizations);
};

const styles = {
  '@global': {
    'html *': {
      boxSizing: 'border-box',
    },
  },
};

export class App extends Component {
  state = {
    user: null,
    client: null,
    renderReady: false,
    theme: createTheme(adaptV4Theme(THEME_DEFAULTS)),
    customizations: null,
  };

  constructor(props) {
    super(props);

    this.Api = get(props, 'api', new Api());
    this.cookies = get(props, 'cookies', new Cookies());

    if (props && props.initialState) {
      // log.debug('constructor', props.initialState);
      this.state = props.initialState;
      // Rebuild the theme
      assign(this.state, buildTheme(this.state.customizations));
    }

    // Always set the Api instance token before anything else renders
    let token = this.cookies.get('CLIENT_TOKEN');
    log.debug('client token!', token);
    this.Api._updateInstanceToken(token);
  }

  prefetchLogos = async customizations => {
    if (customizations) {
      if (customizations.logoDark && customizations.logoDark.id) {
        customizations.logoDark = await fetchMediaContentImage(customizations.logoDark);
      }
      if (customizations.logoLight && customizations.logoLight.id) {
        customizations.logoLight = await fetchMediaContentImage(customizations.logoLight);
      }
    }
    return customizations;
  };

  updateUser = async (token, ttl, { noSetCookie } = {}) => {
    let agency;
    if (token === null || token === undefined) {
      this.Api._updateInstanceToken(null);
      this.cookies.remove('CLIENT_TOKEN', { path: '/' });
      this.setState({ user: null });
    } else if (noSetCookie) {
      // Cookie already set, do not change expiry.
    } else {
      this.cookies.set('CLIENT_TOKEN', token, getCookieOptions(ttl));
    }
    if (token) {
      this.Api._updateInstanceToken(token);

      try {
        let client;
        const { data: user } = await this.Api.userInfo();
        if (user) {
          if (user.principalType !== 'Client') {
            log.error('Client accessed with non-client token:', user.id, user.principalType);
            this.updateUser(null);
            return null;
          }
          // If we got a new token, bang it into place
          // to replace the permanent token we came in
          // with.
          if (user.token) {
            this.Api._updateInstanceToken(token);
            this.cookies.set('CLIENT_TOKEN', user.token, getCookieOptions(ttl));
          }

          agency = user.agency;

          const res = await this.Api.clientInfo(user.userId);
          client = res.data[0];
          const { data: itineraries } = await this.Api.getItineraries('?filter[include][payments]');
          const { data: itineraryMedias } = await this.Api.getItineraryMedia();
          await i18n.changeLanguage(client.language);
          this.setState({ itineraries, itineraryMedias });
        }
        const customizations = get(user, 'agency', {});
        const { theme } = buildTheme(customizations);

        handleTermsUrl(customizations);

        this.setState({ user, client, customizations, theme, agency });
        Sentry.configureScope(scope => {
          scope.setUser({ email: user.email });
          scope.setTag('agencyCode', get(user, 'agency.agencyCode'));
          scope.setTag('layoutName', get(agency, 'layoutName', 'default'));
        });

        return user;
      } catch (err) {
        log.error(`updateUser threw exception: `, err);
        //        this.setState({user: null})
      }
    } else {
      this.setState({ user: null });
      this.setState(buildTheme(this.state.customizations));
      Sentry.configureScope(scope => {
        scope.setUser(null);
        scope.setTag('agencyCode', null);
      });
    }
    return null;
  };

  onAppStart = async serverRender => {
    // If we are server rendering monkeypatch setState
    if (serverRender) {
      this.setState = newState => assign(this.state, newState);
    }
    const token = this.cookies.get('CLIENT_TOKEN');
    if (token !== undefined && token !== 'undefined') {
      await this.updateUser(token, undefined, { noSetCookie: true });
    }

    const params = queryString.parse(this.props.location.search);
    const gaWilderness = pick(params, values(UTM));
    if (!isEmpty(gaWilderness)) {
      // Set pass through GA cookie.
      const gaOptions = {
        path: '/',
      };
      if (!process.env.REACT_APP_API_SERVER.includes('localhost')) {
        gaOptions.domain = '.itrvl.com';
      }
      this.cookies.set(UTM_COOKIE, gaWilderness, gaOptions);
    }

    if (process.env.REACT_APP_GA4_KEY) {
      const itrvlCid = getItrvlCid(this.cookies.get(UTM_GA));
      // '*' casts as String v Int.
      if (itrvlCid) {
        ReactGA.gtag('set', 'user_properties', { itrvl_cid: `${itrvlCid}*` }); // eslint-disable-line camelcase
      }
      if (gaWilderness[UTM.CID]) {
        ReactGA.gtag('set', 'user_properties', { wilderness_cid: `${gaWilderness[UTM.CID]}*` }); // eslint-disable-line camelcase
      }
      ReactGA.gtag('event', 'cid_processed');
    }

    this.setState({ renderReady: true });
  };

  refreshUser = async () => {
    if (this.cookies.get('CLIENT_TOKEN')) {
      const { data: user } = await this.Api.userInfo();
      const res = await this.Api.clientInfo(user.userId);
      const client = res.data[0];
      this.setState({ user, client });
    }
  };

  updateClient = client => {
    this.setState({ client });
  };

  refreshItineraries = async () => {
    try {
      const { data: itineraries } = await this.Api.getItineraries('?filter[include][payments]');
      const { data: itineraryMedias } = await this.Api.getItineraryMedia();
      this.setState({ itineraries, itineraryMedias });
    } catch (err) {
      log.error(`Error refreshing itineraries`, err);
    }
  };

  refreshedItinerary = itinerary => {
    let found = false;
    const itineraries = reduce(
      this.state.itineraries,
      (ret, i) => {
        if (i.id === itinerary.id) {
          found = true;
          ret.push(itinerary);
        } else {
          ret.push(i);
        }
        return ret;
      },
      [],
    );
    if (!found) {
      itineraries.push(itinerary);
    }
    this.setState({ itineraries });
  };

  async componentDidMount() {
    // Don't rerun app start in universal mode
    if (!this.state.renderReady) {
      this.onAppStart(false);
    } else {
      // Make sure theme is loaded
      const customizations = get(this.state.user, 'agency', {});
      const { theme } = buildTheme(customizations);
      this.setState({ theme });
    }
  }

  componentDidUpdate() {
    // ghetto hack so we know the app has hydrated for testing
    if (document && !this.hydrated) {
      const root = document.querySelector('#root');
      if (root) {
        root.classList.add('hydrated');
      }
      this.hydrated = true;
    }
  }

  render() {
    const { theme, renderReady, customizations, agency } = this.state;
    const { location } = this.props;
    const layoutName = location.state && location.state.layoutName ? location.state.layoutName : get(agency, 'layoutName', 'default');
    return (
      //<React.StrictMode>
      <div id="app" data-node-env={process.env.NODE_ENV || 'development'}>
        {renderReady === true ? (
          <ApiContext.Provider value={{ Api: this.Api }}>
            <QueryClientProvider client={queryClient}>
              <UserContext.Provider
                value={{
                  refreshItineraries: () => this.refreshItineraries(),
                  refreshedItinerary: i => this.refreshedItinerary(i),
                  refreshUser: () => this.refreshUser(),
                  updateClient: client => this.updateClient(client),
                  user: this.state.user,
                  client: this.state.client,
                  updateUser: this.updateUser,
                  agency,
                }}
              >
                <ItinerariesContext.Provider
                  value={{
                    itineraries: this.state.itineraries,
                    itineraryMedias: this.state.itineraryMedias,
                    refreshItineraries: () => this.refreshItineraries(),
                    refreshedItinerary: i => this.refreshedItinerary(i),
                  }}
                >
                  <CustomizationsLoader enabled={agency?.jacadaEnhancements}>
                    <MenuProvider>
                      <SnackbarProvider>
                        <ThemeProvider value={{ theme, customizations }}>
                          <ModalProvider>
                            <NiceModal.Provider>
                              <Routes user={this.state.user} layoutName={layoutName} />
                            </NiceModal.Provider>
                          </ModalProvider>
                        </ThemeProvider>
                      </SnackbarProvider>
                    </MenuProvider>
                  </CustomizationsLoader>
                </ItinerariesContext.Provider>
              </UserContext.Provider>
            </QueryClientProvider>
          </ApiContext.Provider>
        ) : (
          <Loading message="Loading..." />
        )}
      </div>
      //</React.StrictMode>
    );
  }
}

let StyledApp = styled(App)(styles);

export default withRouter(StyledApp);
